import { Container, Row } from 'react-bootstrap';
import { conditionAutocomplete, specialtyAutocomplete } from '@medifind/interface';
import {
  StoreFilterAutocomplete,
  StoreFilterFieldInput,
  StoreFilterFieldMultiCheckBox,
  StoreFilterFieldSingleRadio,
} from '@medifind/search-results';
import {
  clearDoctorAnyIdFilterOption,
  clearDoctorNameFilterOption,
  updateDoctorAnyIdFilterOption,
  updateDoctorNameFilterOption,
  useFilterContext,
} from '@medifind/zustand';
import { RadiusFilter } from './RadiusFilter';
import StoreDoctorFilter from './store-filters/StoreDoctorFilter';
import StoreInsuranceFilter from './store-filters/StoreInsuranceFilter';
import StoreLanguageFilter from './store-filters/StoreLanguageFilter';
import styles from './FilterShared.module.scss';

const DoctorResultFilterList = ({ nameAutoComplete }) => {
  const filters = useFilterContext();
  const {
    doctorDoctor,
    doctorCondition,
    doctorSpecialty,
    doctorSecondarySpecialty,
    doctorName,
    doctorTier,
    doctorGender,
    doctorYearsExperience,
    doctorAnyId,
    doctorAvailability,
    doctorSearchRadius,
    doctorLanguage,
    doctorInsurance,
  } = filters.enabledStates;

  return (
    <Container className={styles['result-filter-list__container']}>
      {doctorDoctor && (
        <Row>
          <StoreDoctorFilter {...filters.getFilterOptions('doctorDoctor')} />
        </Row>
      )}

      {doctorAnyId && (
        <Row>
          <StoreFilterFieldInput
            filter={doctorAnyId}
            placeholder="PID/Admin Id/NPI/Mention Id"
            label="ID"
            onInputChange={(input) => updateDoctorAnyIdFilterOption(input)}
            onValueClear={() => clearDoctorAnyIdFilterOption()}
            showClearButton
            {...filters.getFilterOptions('doctorAnyId')}
          />
        </Row>
      )}
      {doctorName && (
        <Row>
          <StoreFilterFieldInput
            filter={doctorName}
            placeholder="Search"
            label="Name"
            onInputChange={(input) => updateDoctorNameFilterOption(input)}
            onValueClear={() => clearDoctorNameFilterOption()}
            showClearButton
            {...filters.getFilterOptions('doctorName')}
          />
        </Row>
      )}
      {doctorSpecialty && filters.getFilterOptions('doctorSpecialty')?.hidden !== true && (
        <Row>
          <StoreFilterAutocomplete
            filter={doctorSpecialty}
            label="Specialty"
            placeholder="Any"
            autocompleteFunction={(input) => specialtyAutocomplete(input, { size: 200 }).catch(() => [])}
            prepopulateOnFocus
            fullWidth
            actLikeASelect
            showClearButton
            {...filters.getFilterOptions('doctorSpecialty')}
          />
        </Row>
      )}
      {doctorSecondarySpecialty && (
        <Row>
          <StoreFilterAutocomplete
            filter={doctorSecondarySpecialty}
            label="Additional Specialty"
            placeholder="Any"
            autocompleteFunction={(input) => specialtyAutocomplete(input, { size: 200 }).catch(() => [])}
            prepopulateOnFocus
            fullWidth
            actLikeASelect
            showClearButton
            {...filters.getFilterOptions('doctorSecondarySpecialty')}
          />
        </Row>
      )}
      {doctorSearchRadius && (
        <Row>
          <RadiusFilter filter={doctorSearchRadius} label="Distance" {...filters.getFilterOptions('doctorSearchRadius')} />
        </Row>
      )}
      {doctorGender && (
        <Row>
          <StoreFilterFieldSingleRadio
            filter={doctorGender}
            label="Gender"
            placeholder="Any"
            isMenuListScroll={false}
            {...filters.getFilterOptions('doctorGender')}
          />
        </Row>
      )}
      {doctorAvailability && (
        <Row>
          <StoreFilterFieldMultiCheckBox
            filter={doctorAvailability}
            label="Availability"
            placeholder="Any"
            isMenuListScroll={false}
            {...filters.getFilterOptions('doctorAvailability')}
          />
        </Row>
      )}
      {doctorLanguage && (
        <Row>
          <StoreLanguageFilter {...filters.getFilterOptions('doctorLanguage')} />
        </Row>
      )}
      {doctorInsurance && (
        <Row>
          <StoreInsuranceFilter {...filters.getFilterOptions('doctorInsurance')} />
        </Row>
      )}
      {doctorYearsExperience && (
        <Row>
          <StoreFilterFieldSingleRadio
            filter={doctorYearsExperience}
            label="Years of Experience"
            placeholder="Any"
            isMenuListScroll={false}
            {...filters.getFilterOptions('doctorYearsExperience')}
          />
        </Row>
      )}
      {doctorTier && (
        <Row>
          <StoreFilterFieldMultiCheckBox
            filter={doctorTier}
            label="Level of Expertise"
            placeholder="Any"
            isMenuListScroll={false}
            {...filters.getFilterOptions('doctorTier')}
          />
        </Row>
      )}
      {doctorCondition && (
        <Row>
          <StoreFilterAutocomplete
            filter={doctorCondition}
            label="Condition"
            placeholder="Search for a condition or procedure..."
            autocompleteFunction={(input) =>
              conditionAutocomplete({ input, showAll: true })
                .then((res) =>
                  res.results.map((result) => ({
                    display: result.display,
                    value: result.projectId,
                    codingType: result.codingType,
                  })),
                )
                .catch(() => [])
            }
            prepopulateOnFocus
            fullWidth
            actLikeASelect
            {...filters.getFilterOptions('doctorCondition')}
          />
        </Row>
      )}
    </Container>
  );
};

export { DoctorResultFilterList as default, DoctorResultFilterList };
